import { useEffect } from "react";
import { useAppDispatch } from "@/redux/features/hooks";
import { useMercureSubscription } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { ExpenseApi } from "@/redux/services/expense/expense.api";
import type { Expense } from "@/redux/services/expense/expense.type";

const EXPENSE_EVENTS = ["expense:created", "expense:voided"] as const;

/**
 * Subscribes to the restaurant's expenses Mercure topic and invalidates the
 * cached expense queries whenever an expense is recorded or voided (possibly by
 * another user on another device), so any open list refetches automatically.
 *
 * Mount once at a high level (DashLayout) — one SSE connection per restaurant.
 *
 * Only subscribes when the user holds `expenses.view`, the permission the API
 * requires for this topic in the Mercure subscriber JWT (SFRESTO-181).
 */
export function useExpensesRealtime(restaurantId: string | undefined) {
  const dispatch = useAppDispatch();
  const { can } = usePermissions();
  const canViewExpenses = can("expenses.view");
  const event = useMercureSubscription<Expense>(
    restaurantId && canViewExpenses
      ? `/api/restaurants/${restaurantId}/expenses`
      : null,
    EXPENSE_EVENTS
  );

  useEffect(() => {
    if (!event) return;
    dispatch(ExpenseApi.util.invalidateTags(["expense"]));
  }, [event, dispatch]);
}
